'use client';

import dynamic from 'next/dynamic';
import React, { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { DonationBnbController } from '@/app/_components/DonationBnbController';
import { GlobalModal } from '@/app/_components/GlobalModal';
import { GlobalNavigation } from '@/app/_components/GlobalNavigation';
import LayoutAsideSection from '@/app/_components/LayoutAsideSection';
import { ToastProvider } from '@/app/_components/ToastProvider';
import { ToastSearchParamsController } from '@/app/_components/ToastSearchParamsController';
import { WelloTracker } from '@/app/_components/WelloTracker';
import { LocalMarketBnbController } from '@/app/welmarket/_components/LocalMarketBnb';
import { SELECTOR } from '@/constants';
import { pretendard } from '@/fonts';
import { useLayoutType } from '@/hooks/useLayoutType';
import { useOverrideLayoutTypeEffect } from '@/hooks/useOverrideLayoutTypeEffect';
import { useGlobalStore } from '@/stores/GlobalStore';

import {
  GLOBAL_LAYOUT_CONTAINER_CLASS,
  GLOBAL_LAYOUT_MAIN_CLASS,
  GLOBAL_LAYOUT_MAIN_WRAPPER_CLASS,
  GLOBAL_LAYOUT_PAGE_NAV_WRAPPER_CLASS,
  HIDE_DONATION_HEADER_PATHNAMES,
} from './constants';
import { ResponsiveDialogContainer } from './ResponsiveDialogContainer';

// SSR 비활성화로 App Router 마운트 이슈 해결
const NaverCtsTracker = dynamic(
  () =>
    import('./NaverCtsTracker').then((mod) => ({
      default: mod.NaverCtsTracker,
    })),
  { ssr: false },
);

interface GlobalLayoutProps {
  children: React.ReactNode;
}

export const GlobalLayout = ({ children }: GlobalLayoutProps) => {
  useOverrideLayoutTypeEffect();

  const { layoutType, pathname } = useLayoutType();

  const isDonationHeaderShow =
    useGlobalStore((state) => state.isDonationDesktopHeaderShow) &&
    (pathname?.startsWith('/donation') || pathname?.startsWith('/welmarket')) &&
    !HIDE_DONATION_HEADER_PATHNAMES.includes(pathname ?? '');

  return (
    <ErrorBoundary fallback={null}>
      <Suspense>
        <div
          className={GLOBAL_LAYOUT_CONTAINER_CLASS}
          style={pretendard.style}
          //* 이에 따라 donation-layout.scss, wello-layout.scss 스타일이 적용됨
          data-layout={layoutType}
          data-donation-header={isDonationHeaderShow ? 'show' : 'hide'}
        >
          <a
            href={`#${SELECTOR.MAIN}`}
            className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[9999] focus:rounded-small focus:bg-Gray-black focus:px-16 focus:py-8 focus:font-600 focus:text-Gray-white focus:typo-body4"
          >
            본문 바로가기
          </a>
          <LayoutAsideSection />
          <div
            className={GLOBAL_LAYOUT_MAIN_WRAPPER_CLASS}
            id={SELECTOR.MAIN_WRAPPER}
          >
            <div
              className={GLOBAL_LAYOUT_PAGE_NAV_WRAPPER_CLASS}
              id={SELECTOR.PAGE_NAV_WRAPPER}
            />
            <ResponsiveDialogContainer />
            <ToastProvider>
              <ToastSearchParamsController />
              <main className={GLOBAL_LAYOUT_MAIN_CLASS} id={SELECTOR.MAIN}>
                {children}
                <NaverCtsTracker />
              </main>
            </ToastProvider>
            <WelloTracker />
            <GlobalModal />
            <GlobalNavigation />
            <DonationBnbController />
            <LocalMarketBnbController />
          </div>
        </div>
      </Suspense>
    </ErrorBoundary>
  );
};
